import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChartColumn, Clock, Gamepad2, Play, Search, Trophy } from 'lucide-react';
import { api } from '../../../api';
import config from '../../../config';
import { useAuth } from '../../../contexts/AuthContext';
import { useCoinBalance } from '../../../contexts/CoinBalanceContext';
import useGComprisProgress from '../../../hooks/useGComprisProgress';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { GCOMPRIS_ACTIVITIES } from '../../../data/gcomprisCatalog';
import {
  GCOMPRIS_CATEGORIES,
  GCOMPRIS_LOGO_URL,
  filterActivities,
  formatPlayTime,
  gcomprisCategoryIconUrl,
  gcomprisIconUrl,
  makeSessionId,
} from '../../../utils/gcompris';
import GComprisCategoryStrip from './GComprisCategoryStrip';
import GComprisCoinBadge from './GComprisCoinBadge';
import GComprisHelpModal from './GComprisHelpModal';
import GComprisLevelsPanel from './GComprisLevelsPanel';

function StatTile({ label, value, Icon, tint }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <div className="min-w-0">
        <p className="text-2xl font-extrabold leading-tight text-slate-900 tabular-nums">{value}</p>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      </div>
      <span className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${tint}`}>
        <Icon size={22} />
      </span>
    </div>
  );
}

// The coin badge is what the level to play next pays (none once every level is done).
function ActivityCard({ activity, progress, coinsForLevel, selected, onSelect }) {
  const finished = Boolean(activity.levels) && progress.nextLevel > activity.levels;
  const nextCoins = finished ? 0 : coinsForLevel(progress.nextLevel);
  return (
    <button
      type="button"
      onClick={() => onSelect(activity.name)}
      aria-pressed={selected}
      title={activity.title}
      className={`flex flex-col items-center gap-2 rounded-2xl border-2 bg-gradient-to-b from-sky-50 to-emerald-50 p-3 text-center transition hover:-translate-y-0.5 hover:shadow-md ${
        selected ? 'border-orange-400 shadow-md' : 'border-emerald-100'
      }`}
    >
      <img src={gcomprisIconUrl(activity.name)} alt="" loading="lazy" className="h-16 w-full object-contain" />
      {/* Exactly two lines tall (leading-tight = 1.25): a taller box would let a clamped third line peek out. */}
      <span className="line-clamp-2 h-[2.5em] text-[13px] font-extrabold leading-tight text-slate-800">{activity.title}</span>
      <span className="mt-auto flex flex-wrap items-center justify-center gap-1.5">
        <span className="rounded-lg bg-emerald-100 px-2 py-0.5 text-[12px] font-bold text-emerald-700">
          {finished ? 'Completed' : `Level ${progress.nextLevel}`}
        </span>
        {nextCoins > 0 && <GComprisCoinBadge amount={nextCoins} />}
      </span>
    </button>
  );
}

/**
 * Fills the Learning Zone main content area (sidebar and top bar stay put)
 * with GCompris itself, running client-side via its WebAssembly build - no
 * native app, no separate window. The game reports its events (level completed
 * -> coins and the next level) to POST /api/gcompris/events, like the EduBridge
 * path: embed.html gets the student, a session id, a reporting token and the
 * events URL in the URL fragment (never sent to the server). Without a token
 * the game still opens, it just can't report.
 */
function GComprisEmbedPanel({ activity, level, onClose }) {
  const { user } = useAuth();
  const studentId = String(user?._id || user?.id || '');
  const [reporting, setReporting] = useState(null); // the URL fragment, once the token is ready

  useEffect(() => {
    let cancelled = false;
    const prepare = async () => {
      let token = localStorage.getItem('token') || '';
      try {
        const response = await api.post('/api/gcompris/report-token', { purpose: 'browser' });
        token = response.data?.token || token;
      } catch (error) {
        // Same fallback as the EduBridge launch: the login token can report too.
      }
      if (cancelled) return;
      setReporting(new URLSearchParams({
        studentId,
        sessionId: makeSessionId(),
        token,
        reportUrl: `${config.API_BASE_URL}/api/gcompris/events`,
      }).toString());
    };
    prepare();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const params = new URLSearchParams({ activity: activity.name, level: String(level) });
  return (
    <div
      className="flex h-full max-h-[calc(100vh-180px)] flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm"
    >
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
        >
          <ArrowLeft size={16} />
          Back to games
        </button>
        <img src={gcomprisIconUrl(activity.name)} alt="" className="h-7 w-7 object-contain" />
        <span className="truncate text-sm font-extrabold text-slate-800">{activity.title}</span>
      </div>
      {reporting === null ? (
        <p className="flex min-h-0 flex-1 items-center justify-center text-sm font-semibold text-slate-500">Loading GCompris...</p>
      ) : (
        <iframe
          title={`GCompris - ${activity.title}`}
          src={`${config.API_BASE_URL}/gcompris-wasm/embed.html?${params.toString()}#${reporting}`}
          className="min-h-0 flex-1 border-0"
          allow="autoplay"
        />
      )}
    </div>
  );
}

/**
 * Computer Apps > GCompris, inside the Learning Zone. Shows every GCompris
 * activity (real names, descriptions, categories and artwork read from the
 * GCompris install - see scripts/generateGComprisCatalog.js), filterable by
 * GCompris' own categories. Picking an activity opens its levels panel, and
 * Play embeds GCompris itself (its WebAssembly build) in the main content area.
 */
export default function GComprisZone() {
  const { refreshBalance } = useCoinBalance();
  const { loading, stats, progressFor, rewardsFor, refresh } = useGComprisProgress();
  // Wide screens dock the levels panel beside the grid (as in the design); narrower ones use a modal.
  const docked = useMediaQuery('(min-width: 1280px)');

  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedName, setSelectedName] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [activeActivity, setActiveActivity] = useState(null);
  const [activeLevel, setActiveLevel] = useState(1);

  const activities = useMemo(
    () => filterActivities(GCOMPRIS_ACTIVITIES, { category, query }),
    [category, query]
  );
  const selected = GCOMPRIS_ACTIVITIES.find((activity) => activity.name === selectedName) || null;
  const categoryLabel = GCOMPRIS_CATEGORIES.find((item) => item.id === category).label;

  const closeLevels = useCallback(() => setSelectedName(null), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const closeActivity = useCallback(() => {
    setActiveActivity(null);
    refresh();
    refreshBalance();
  }, [refresh, refreshBalance]);

  const play = (activity) => {
    setActiveActivity(activity);
    const { nextLevel } = progressFor(activity.name);
    // Every level done: replay from the start rather than asking GCompris for a level that doesn't exist.
    setActiveLevel(activity.levels && nextLevel > activity.levels ? 1 : nextLevel);
    // The modal would sit on top of the embedded game.
    if (!docked) setSelectedName(null);
  };

  const levelsPanel = selected && (
    <GComprisLevelsPanel
      activity={selected}
      progress={progressFor(selected.name)}
      {...rewardsFor(selected.name)}
      docked={docked}
      onPlay={() => play(selected)}
      onClose={closeLevels}
    />
  );

  const display = (value) => (loading ? '–' : value);

  if (activeActivity) {
    return <GComprisEmbedPanel activity={activeActivity} level={activeLevel} onClose={closeActivity} />;
  }

  return (
    <>
      {helpOpen && <GComprisHelpModal onClose={closeHelp} />}

      <div className="flex items-center gap-4 mb-6">
        <span className="text-5xl leading-none" aria-hidden="true">💻</span>
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 leading-tight">Computer Apps</h1>
          <p className="text-[15px] font-medium text-gray-500">Learn. Practice. Create. Grow!</p>
        </div>
      </div>

      <section className="mb-5 flex flex-wrap items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <img src={GCOMPRIS_LOGO_URL} alt="GCompris" className="h-16 w-auto shrink-0" />
        <div className="min-w-[220px] flex-1">
          <h2 className="text-xl font-extrabold text-slate-900">GCompris</h2>
          <p className="text-sm font-medium text-slate-600">
            Fun educational activities for Math, Reading, Sciences, Puzzles, Strategy and more!
          </p>
        </div>
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-50 px-5 py-2.5 text-sm font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
        >
          <span className="h-6 w-6 rounded-full bg-indigo-600 text-white flex items-center justify-center">
            <Play size={12} fill="currentColor" />
          </span>
          How to Use
        </button>
      </section>

      <GComprisCategoryStrip category={category} onSelect={setCategory} />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Activities played" value={display(stats.activitiesPlayed)} Icon={Gamepad2} tint="bg-indigo-50 text-indigo-500" />
        <StatTile label="Sessions" value={display(stats.sessions)} Icon={ChartColumn} tint="bg-emerald-50 text-emerald-500" />
        <StatTile label="Levels completed" value={display(stats.levelsCompleted)} Icon={Trophy} tint="bg-amber-50 text-amber-500" />
        <StatTile label="Play time" value={display(formatPlayTime(stats.playTimeMs))} Icon={Clock} tint="bg-sky-50 text-sky-500" />
      </div>

      <div className="flex items-start gap-4">
        <section className="flex-1 min-w-0 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <img src={gcomprisCategoryIconUrl(category)} alt="" className="h-12 w-12 shrink-0 object-contain" />
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-extrabold leading-tight text-slate-900">
                {categoryLabel} Activities
                <span className="ml-2 align-middle rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">
                  {activities.length}
                </span>
              </h2>
              <p className="text-sm font-medium text-gray-500">Click on an activity to start learning!</p>
            </div>
            <label className="relative block w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search activities"
                aria-label="Search activities"
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </label>
          </div>

          {activities.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm font-semibold text-gray-500">
              No activities match your search.
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(148px,1fr))] gap-3">
              {activities.map((activity) => (
                <ActivityCard
                  key={activity.name}
                  activity={activity}
                  progress={progressFor(activity.name)}
                  coinsForLevel={rewardsFor(activity.name).coinsForLevel}
                  selected={activity.name === selectedName}
                  onSelect={setSelectedName}
                />
              ))}
            </div>
          )}
        </section>

        {docked && levelsPanel && <div className="sticky top-0 w-[380px] shrink-0">{levelsPanel}</div>}
      </div>

      {!docked && levelsPanel}
    </>
  );
}
